import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {NavigationExtras, Router} from '@angular/router';
import {BehaviorSubject, Observable} from 'rxjs';
import {en_US, NzI18nService, vi_VN} from 'ng-zorro-antd/i18n';

/**
 * Service to manage locale and locale-prefixed navigation throughout the application.
 * Supports path-based localization (e.g., /en/..., /vi/...).
 */
@Injectable({
  providedIn: 'root'
})
export class LocaleService {
  private currentLocaleSubject = new BehaviorSubject<string>('en');

  /** List of supported locales */
  readonly supportedLocales = [
    {code: 'en', name: 'English'},
    {code: 'vi', name: 'Tiếng Việt'}
  ];

  constructor(
    private translateService: TranslateService,
    private router: Router,
    private nzI18n: NzI18nService
  ) {
    // Initialize with URL locale, sessionStorage value, or default to English
    const urlLocale = this.getLocaleFromUrl();
    const savedLocale = sessionStorage.getItem('locale');
    const initialLocale = urlLocale || savedLocale || 'en';

    this.setLocale(initialLocale);
  }

  /**
   * Get the current locale
   */
  getCurrentLocale(): string {
    return this.currentLocaleSubject.getValue();
  }

  /**
   * Get the current locale as an Observable
   */
  getCurrentLocale$(): Observable<string> {
    return this.currentLocaleSubject.asObservable();
  }

  /**
   * Extract locale code from the current URL path.
   * Returns the locale code if found (e.g., 'en' from '/en/about'), null otherwise.
   */
  getLocaleFromUrl(): string | null {
    const path = window.location.pathname;
    const segments = path.split('/').filter(s => s.length > 0);
    const firstSegment = segments[0];

    if (firstSegment && this.supportedLocales.some(lang => lang.code === firstSegment)) {
      return firstSegment;
    }

    return null;
  }

  /**
   * Set the application locale
   * @param locale Locale code to set
   */
  setLocale(locale: string): void {
    // Verify it's a supported locale
    if (!this.supportedLocales.some(l => l.code === locale)) {
      console.warn(`Locale '${locale}' is not supported. Defaulting to English.`);
      locale = 'en';
    }

    // Update translate service
    this.translateService.use(locale);

    // Keep ng-zorro (pagination, date picker, etc.) in sync with app language
    this.nzI18n.setLocale(locale === 'vi' ? vi_VN : en_US);

    // Save to sessionStorage for persistence using unified 'locale' key
    sessionStorage.setItem('locale', locale);

    // Update state
    this.currentLocaleSubject.next(locale);
  }

  /**
   * Toggle between supported locales
   * Currently toggles between English and Vietnamese
   */
  toggleLocale(): void {
    const current = this.getCurrentLocale();
    const next = current === 'en' ? 'vi' : 'en';
    this.switchLocale(next);
  }

  /**
   * Switch locale and update the URL path.
   * Navigates from /en/current-path to /vi/current-path (or vice versa).
   * @param locale Target locale code
   */
  switchLocale(locale: string): void {
    // Verify it's a supported locale
    if (!this.supportedLocales.some(l => l.code === locale)) {
      console.warn(`Locale '${locale}' is not supported. Defaulting to English.`);
      locale = 'en';
    }

    const currentLocale = this.getCurrentLocale();
    if (locale === currentLocale) {
      return; // No change needed
    }

    // Update the locale setting
    this.setLocale(locale);

    // Update the URL to reflect the new locale
    const currentUrl = this.router.url;
    const newUrl = this.replaceLocaleInUrl(currentUrl, locale);

    this.router.navigateByUrl(newUrl);
  }

  /**
   * Navigate to a path with the current locale prefix.
   * Supports absolute paths, relative paths, and home navigation.
   *
   * @param path Path to navigate to (without locale prefix)
   *   - Empty string or empty array: Navigate to home (/en/ or /vi/)
   *   - String: Absolute path like 'pricing' or 'get-data/data-domains/dataset-groups'
   *   - Array: Path segments like ['pricing', 'package', 'checkout']
   *   - Array with '..': Relative navigation like ['..', 'data-domains', 'dataset-groups'] (relative to current route)
   * @param extras Optional navigation extras (e.g., queryParams, relativeTo)
   *
   * @example
   * // Navigate to home
   * navigate('') or navigate([])
   *
   * // Navigate to /en/pricing
   * navigate('pricing')
   *
   * // Navigate to /en/pricing/package/checkout
   * navigate(['pricing', 'package', 'checkout'])
   *
   * // Navigate to /en/get-data/data-domains/dataset-groups
   * navigate(['get-data', 'data-domains', 'dataset-groups'])
   *
   * // Navigate to /en/get-data/data-domains/dataset-groups/2
   * navigate(['get-data', 'data-domains', 'dataset-groups', '2'])
   *
   * // Relative navigation (from .../data-domains/dataset-groups/2 to .../data-domains/dataset-groups)
   * navigate(['..', 'dataset-groups'], {relativeTo: route})
   */
  navigate(path: string | string[], extras?: NavigationExtras): Promise<boolean> {
    const locale = this.getCurrentLocale();

    // Handle empty path - navigate to home
    if (!path || (Array.isArray(path) && path.length === 0) || (typeof path === 'string' && path.trim() === '')) {
      return this.router.navigate(['/', locale], extras);
    }

    if (Array.isArray(path)) {
      // If relativeTo is provided, treat as relative navigation
      // Angular Router will handle relative paths (e.g., ['..', 'parent'])
      // The locale prefix is already in the current route
      if (extras?.relativeTo) {
        return this.router.navigate(path, extras);
      }

      // Absolute path with locale prefix
      return this.router.navigate(['/', locale, ...path], extras);
    }

    // String path - treat as absolute
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return this.router.navigateByUrl(`/${locale}/${cleanPath}`, extras);
  }

  /**
   * Replace the locale segment in a URL path.
   * @param url Current URL
   * @param newLocale New locale code
   */
  private replaceLocaleInUrl(url: string, newLocale: string): string {
    const supportedLocales = this.supportedLocales.map(l => l.code);

    // Split URL into path and query string
    const [path, queryString] = url.split('?');
    const segments = path.split('/').filter(s => s.length > 0);

    // Check if first segment is a locale code
    if (segments.length > 0 && supportedLocales.includes(segments[0])) {
      segments[0] = newLocale;
    } else {
      // No locale prefix found, add it
      segments.unshift(newLocale);
    }

    const newPath = '/' + segments.join('/');
    return queryString ? `${newPath}?${queryString}` : newPath;
  }
}
